import React, { useState } from 'react';
import type {
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import { useHasEnteredViewport } from 'src/modules/shared/hooks/use-has-entered-viewport';
import { buildWebsiteTemplatePreviewUrl } from '../../utils/template-preview.util';
import { WebsiteScaledViewport } from './website-scaled-viewport';
import { WebsiteTemplateFallbackPreview } from './website-template-fallback-preview';

/** Desktop width at which design thumbnails render before scaling. */
const THUMBNAIL_BASE_WIDTH = 1280;
/** Desktop height at which design thumbnails render before scaling. */
const THUMBNAIL_BASE_HEIGHT = 800;
/** Width at which the descriptor-based fallback renders before scaling. */
const FALLBACK_BASE_WIDTH = 560;
/** Height at which the descriptor-based fallback renders before scaling. */
const FALLBACK_BASE_HEIGHT = 350;

/** Props for a design card thumbnail. */
interface WebsiteTemplateLiveThumbnailProps {
  /** Design whose mock preview fills the thumbnail. */
  template: WebsiteTemplateRegistryTemplateSummary;
  /** Draft whose brand values personalize the preview. */
  form: WebsiteUpsertRequest;
}

/**
 * Shows a live, scaled-down mock preview of a design with the agency's brand.
 *
 * The iframe mounts only once the card scrolls near the viewport, shows a
 * skeleton until it loads, and ignores pointer and keyboard input so the card
 * actions stay in charge. Designs without a mock preview get the
 * descriptor-based fallback instead.
 *
 * @param props - Design and draft for the preview.
 * @returns A 16:10 thumbnail.
 */
export const WebsiteTemplateLiveThumbnail: React.FC<
  WebsiteTemplateLiveThumbnailProps
> = ({ template, form }) => {
  const { t } = useI18n();
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const hasEnteredViewport = useHasEnteredViewport(frame);
  const [loadedUrl, setLoadedUrl] = useState<string>();
  const previewUrl = buildWebsiteTemplatePreviewUrl({ template, form });

  if (!previewUrl) {
    return (
      <div aria-hidden="true" className="pointer-events-none bg-white">
        <WebsiteScaledViewport
          baseWidth={FALLBACK_BASE_WIDTH}
          baseHeight={FALLBACK_BASE_HEIGHT}
        >
          <WebsiteTemplateFallbackPreview template={template} form={form} />
        </WebsiteScaledViewport>
      </div>
    );
  }

  const isLoaded = loadedUrl === previewUrl;

  return (
    <div ref={setFrame} className="relative bg-neutral-100">
      <WebsiteScaledViewport
        baseWidth={THUMBNAIL_BASE_WIDTH}
        baseHeight={THUMBNAIL_BASE_HEIGHT}
      >
        {hasEnteredViewport ? (
          <iframe
            title={t('websites.settings.design.thumbnailTitle', {
              templateName: template.name,
            })}
            src={previewUrl}
            width={THUMBNAIL_BASE_WIDTH}
            height={THUMBNAIL_BASE_HEIGHT}
            className="pointer-events-none block border-0 bg-white"
            tabIndex={-1}
            aria-hidden="true"
            loading="lazy"
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            onLoad={() => setLoadedUrl(previewUrl)}
          />
        ) : null}
      </WebsiteScaledViewport>
      <div
        aria-hidden="true"
        data-testid="website-template-thumbnail-skeleton"
        className={classes(
          'absolute inset-0 bg-neutral-100 transition-opacity duration-300',
          isLoaded
            ? 'pointer-events-none opacity-0'
            : 'animate-pulse opacity-100'
        )}
      />
    </div>
  );
};
